import type { EmpresaDetailed } from '@/types'
import { Input } from '@/components/ui/Input'

interface EmpresaFinanceiroTabProps {
  empresa: EmpresaDetailed
  editing?: boolean
  editData?: Partial<EmpresaDetailed>
  onEditDataChange?: (data: Partial<EmpresaDetailed>) => void
}

const formatCNPJ = (value: string) => {
  const v = value.replace(/\D/g, '').slice(0, 14)
  if (v.length > 12) return v.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
  if (v.length > 8) return v.replace(/^(\d{2})(\d{3})(\d{3})(\d{0,4})/, '$1.$2.$3/$4')
  if (v.length > 5) return v.replace(/^(\d{2})(\d{3})(\d{0,3})/, '$1.$2.$3')
  if (v.length > 2) return v.replace(/^(\d{2})(\d{0,3})/, '$1.$2')
  return v
}

export function EmpresaFinanceiroTab({ empresa, editing, editData, onEditDataChange }: EmpresaFinanceiroTabProps) {
  const renderField = (label: string, key: keyof EmpresaDetailed, placeholder?: string) => {
    if (editing && onEditDataChange && editData) {
      const fieldValue = editData[key] ?? ''
      return (
        <div className='flex flex-col gap-1.5'>
          <label
            className='text-[var(--font-color-label)] uppercase tracking-wider font-medium'
            style={{ fontSize: 'var(--font-size-label)' }}
          >
            {label}
          </label>
          <Input
            value={String(fieldValue)}
            onChange={(e) => {
              const val = key === 'cnpj' ? formatCNPJ(e.target.value) : e.target.value
              onEditDataChange({ ...editData, [key]: val })
            }}
            placeholder={placeholder}
            className='text-sm w-full'
            maxLength={key === 'cnpj' ? 18 : undefined}
          />
        </div>
      )
    }

    const value = empresa[key] ?? '—'
    return (
      <div className='flex flex-col gap-1'>
        <p
          className='text-[var(--font-color-label)] uppercase tracking-wider font-medium'
          style={{ fontSize: 'var(--font-size-label)' }}
        >
          {label}
        </p>
        <p
          className='text-[var(--font-color-body)]'
          style={{ fontSize: 'var(--font-size-sm)' }}
        >
          {String(value)}
        </p>
      </div>
    )
  }

  return (
    <div className='space-y-6'>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
        {/* Left Column: Fiscal Data & Signature Owner */}
        <div className='space-y-6'>
          <div>
            <h4
              className='text-[var(--font-color-label)] uppercase tracking-wider font-semibold mb-3 border-b border-[var(--glass-border)] pb-1.5'
              style={{ fontSize: 'var(--font-size-sm)' }}
            >
              Dados Fiscais
            </h4>
            {renderField('CNPJ', 'cnpj', '00.000.000/0000-00')}
          </div>

          <div>
            <h4
              className='text-[var(--font-color-label)] uppercase tracking-wider font-semibold mb-3 border-b border-[var(--glass-border)] pb-1.5'
              style={{ fontSize: 'var(--font-size-sm)' }}
            >
              Responsável pela Assinatura
            </h4>
            <div className='space-y-3'>
              {renderField('Nome Completo', 'responsavelAssinaturaNome')}
              {renderField('E-mail', 'responsavelAssinaturaEmail')}
            </div>
          </div>
        </div>

        {/* Right Column: Financial Responsable */}
        <div>
          <h4
            className='text-[var(--font-color-label)] uppercase tracking-wider font-semibold mb-3 border-b border-[var(--glass-border)] pb-1.5'
            style={{ fontSize: 'var(--font-size-sm)' }}
          >
            Responsável Financeiro (Boletos)
          </h4>
          <div className='space-y-3'>
            {renderField('Nome do Contato', 'responsavelFinanceiroNome')}
            {renderField('E-mail Financeiro', 'responsavelFinanceiroEmail')}
          </div>
        </div>
      </div>
    </div>
  )
}
